/**
 * DocumentValidationHeader - Header component for validation page
 *
 * Displays session info, expiry timer, and action buttons
 */

import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, CheckSquare, Loader2, Save, Square, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface DocumentValidationHeaderProps {
  sessionId: string;
  documentId: string;
  transactionId: string;
  expiresAt: string;
  isDirty: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  onSelectAll?: () => void;
  onDeselectAll?: () => void;
}

export function DocumentValidationHeader({
  sessionId,
  documentId: _documentId,
  transactionId,
  expiresAt,
  isDirty,
  isSubmitting,
  onSubmit,
  onCancel,
  onSelectAll,
  onDeselectAll,
}: DocumentValidationHeaderProps) {
  const timeRemaining = formatDistanceToNow(new Date(expiresAt), { addSuffix: true });
  const isExpired = new Date(expiresAt) < new Date();

  return (
    <div className="mb-8">
      {/* Title and Session Info */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Validate Document Data</h1>
          <p className="text-muted-foreground">
            Review the extracted data and select which fields to save to the database.
          </p>
        </div>
        {isDirty && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 rounded-lg text-sm">
            <AlertCircle className="h-4 w-4" />
            <span>Unsaved changes</span>
          </div>
        )}
      </div>

      {/* Session Info Bar */}
      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border">
        <div className="flex items-center gap-6 text-sm">
          <div>
            <span className="text-muted-foreground">Session ID:</span>{' '}
            <code className="text-xs">{sessionId.slice(0, 8)}...</code>
          </div>
          <div>
            <span className="text-muted-foreground">Transaction ID:</span>{' '}
            <code className="text-xs">{transactionId.slice(0, 8)}...</code>
          </div>
          <div className={isExpired ? 'text-destructive' : ''}>
            <span className="text-muted-foreground">Expires:</span>{' '}
            {isExpired ? (
              <span className="font-medium">Expired</span>
            ) : (
              <span className="font-medium">{timeRemaining}</span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onDeselectAll} disabled={isSubmitting}>
            <Square className="h-4 w-4 mr-2" />
            Deselect All
          </Button>
          <Button variant="outline" size="sm" onClick={onSelectAll} disabled={isSubmitting}>
            <CheckSquare className="h-4 w-4 mr-2" />
            Select All
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting || isExpired}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Selected Data
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Expiry Warning */}
      {isExpired && (
        <div className="mt-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm font-medium">
            This validation session has expired. Please start a new document processing session.
          </p>
        </div>
      )}
    </div>
  );
}
